import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowUpRightIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Project } from '../data/projects';
import { DeepwatchHero } from './DeepwatchHero';
type ProjectCardProps = {
  project: Project;
  index: number;
};
export function ProjectCard({
  project,
  index
}: ProjectCardProps) {
  const reduceMotion = useReducedMotion();
  const isFeatured = Boolean(project.featured);
  const customThumb = project.slug === 'deepwatch' ? <DeepwatchHero data-id="element-366" data-mp="2lj85lp7wr19z" /> : null;
  return <motion.article initial={reduceMotion ? false : {
    opacity: 0,
    y: 18
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-80px'
  }} transition={{
    duration: 0.28,
    delay: index * 0.04,
    ease: [0.22, 1, 0.36, 1]
  }} className={isFeatured ? 'col-span-1 md:col-span-2' : 'col-span-1'} data-id="element-367" data-mp="2li6ph6c1bole">
      <Link to={`/work/${project.slug}`} className="group block focus-visible:outline-none" data-id="element-368" data-mp="105diakohx46f">
        <div className={`relative mb-5 overflow-hidden bg-border ${isFeatured ? 'aspect-[16/10] md:aspect-[2.1/1]' : 'aspect-[4/3] md:aspect-[16/10]'}`} data-id="element-369" data-mp="1gcvbsxkoar6">
          {customThumb ? <div className="absolute inset-0 transition-transform duration-300 ease-out group-hover:scale-[1.015]" data-id="element-370" data-mp="ah2pks76pt2s">
              {customThumb}
            </div> : <>
              {project.color && project.slug !== 'winnow' && <div aria-hidden="true" className="absolute inset-0 z-10 opacity-10 mix-blend-multiply transition-opacity duration-200 group-hover:opacity-0" style={{
            backgroundColor: project.color
          }} data-id="element-371" data-mp="kdnvrguxxa87" />}
              <img src={project.heroImage} alt={project.title} className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.015]" data-id="element-372" data-mp="p6qojmrw8fnc" />
            </>}
          <div className="absolute inset-0 ring-1 ring-inset ring-foreground/0 transition-colors duration-200 group-hover:ring-cobalt group-focus-visible:ring-2 group-focus-visible:ring-cobalt" data-id="element-373" data-mp="3rzes4zr7gwht" />
        </div>

        <div className={`grid gap-5 ${isFeatured ? 'lg:grid-cols-[minmax(0,1fr)_15rem] lg:gap-12' : 'lg:grid-cols-[minmax(0,1fr)_10rem] lg:gap-6'}`} data-id="element-374" data-mp="4tx7zd180a9v">
          <div className="min-w-0" data-id="element-375" data-mp="9myplsstanx5">
            <p className="mb-2 font-display text-sm font-medium uppercase tracking-[0.1em] text-muted" data-id="element-376" data-mp="eki6l0gw9ess">
              {project.category}
            </p>
            <h3 className={`break-words font-medium leading-[1.03] tracking-[-0.03em] transition-colors duration-200 group-hover:text-cobalt ${isFeatured ? 'text-3xl md:text-5xl' : 'text-3xl md:text-4xl'}`} data-id="element-377" data-mp="jl67cduxda07">
              {project.title}
            </h3>
            <p className={`mt-4 max-w-2xl break-words leading-relaxed text-muted ${isFeatured ? 'text-base md:text-lg' : 'text-base'}`} data-id="element-378" data-mp="2pcthuegrw6al">
              {project.description}
            </p>
          </div>

          <div className="flex items-end justify-between gap-4 lg:flex-col lg:items-end lg:justify-between lg:text-right" data-id="element-379" data-mp="2msnbk04os3qv">
            <div className="font-display text-sm font-medium uppercase tracking-[0.08em] text-muted" data-id="element-380" data-mp="2hzar8271ij3x">
              <span className="block text-foreground" data-id="element-381" data-mp="2d224rpeuzswv">{project.client}</span>
              <span data-id="element-382" data-mp="283qy1njv1yiv">{project.year}</span>
            </div>
            <span className="inline-flex items-center gap-2 font-display text-sm font-medium uppercase tracking-[0.1em] text-foreground transition-colors duration-200 group-hover:text-cobalt" data-id="element-383" data-mp="35qg6og92j9dp">
              View case study
              <ArrowUpRightIcon size={16} aria-hidden="true" className="transition-transform duration-200 ease-out group-hover:-translate-y-0.5 group-hover:translate-x-0.5" data-id="element-384" data-mp="2vrijphrajnwt" />
            </span>
          </div>
        </div>
      </Link>
    </motion.article>;
}